import React from "react";
import PropTypes from "prop-types";

/**
 * NavigationItem Component
 * Renders an individual navigation item with icon, label, and interaction handling
 */
function NavigationItem({
  item,
  isActive,
  isCollapsed = false,
  onClick = null,
  className = "",
}) {
  // Early return if item is not provided
  if (!item) {
    // NavigationItem: item prop is required
    return null;
  }

  const handleClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  const handleKeyDown = event => {
    // Handle keyboard navigation (Enter and Space)
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      className={`sidebar-nav-item ${isActive ? "active" : ""} ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      title={isCollapsed ? item.label : undefined}
      aria-label={`${item.label}${isActive ? " (current page)" : ""}`}
      aria-current={isActive ? "page" : undefined}
      type="button"
      data-testid={`nav-item-${item.id}`}
      data-active={isActive}
    >
      <span
        className="material-symbols-outlined sidebar-nav-icon sidebar-nav-icon-material-symbols"
        aria-hidden="true"
      >
        {item.icon}
      </span>
      {!isCollapsed && <span className="sidebar-nav-label">{item.label}</span>}
      {item.badgeCount > 0 && (
        <span
          className="sidebar-nav-badge"
          aria-label={`${item.badgeCount} unread messages`}
        >
          {Math.min(item.badgeCount, 99)}
        </span>
      )}
      {isActive && <span className="sr-only">Current page</span>}
    </button>
  );
}

NavigationItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string).isRequired,
    badgeCount: PropTypes.number,
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  isCollapsed: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default NavigationItem;
